import {
  createInitialNavagrahaEntries,
  LAGNA_RASHI_OPTIONS,
  NAVAGRAHA_CALC_OPTIONS,
} from '../data/jyotishaUcchNeechCalculator';
import { isValidDegreeInSign } from '../data/jyotishaDegreeInSign';
import { houseFromSign } from './jyotishaVargaCalculation';
import { yogaChartPlanet } from '../data/jyotishaIntroYogaChartDemos';
import { createNorthIndianChartData } from './kundali-chart';

export const SYLLABUS_CHART_LAB_STORAGE_KEY = 'parashari_syllabus_chart_lab_v1';

const GRAHA_EN = {
  sun: 'Sun',
  moon: 'Moon',
  mars: 'Mars',
  mercury: 'Mercury',
  jupiter: 'Jupiter',
  venus: 'Venus',
  saturn: 'Saturn',
  rahu: 'Rahu',
  ketu: 'Ketu',
};

export const NAVAGRAHA_PLACEMENT_ORDER = NAVAGRAHA_CALC_OPTIONS.map((g) => g.value);

const SIGN_LORD_EN = [
  'Mars',
  'Venus',
  'Mercury',
  'Moon',
  'Sun',
  'Mercury',
  'Venus',
  'Mars',
  'Jupiter',
  'Saturn',
  'Saturn',
  'Jupiter',
];

/**
 * First unplaced graha after `afterKey` in placement order; wraps the list.
 * If every graha is placed, returns `afterKey` or the first in order.
 * @param {Record<string, number>} placements
 * @param {string | null} [afterKey]
 */
export function nextUnplacedGraha(placements, afterKey = null) {
  const order = NAVAGRAHA_PLACEMENT_ORDER;
  const start = afterKey ? order.indexOf(afterKey) : -1;
  if (start < 0) {
    return order.find((k) => !placements[k]) ?? order[0];
  }
  for (let i = 1; i <= order.length; i += 1) {
    const key = order[(start + i) % order.length];
    if (!placements[key]) return key;
  }
  return afterKey ?? order[0];
}

/** Sun and Moon are never retrograde in Parashari practice charts. */
export const NAVAGRAHA_NEVER_RETROGRADE = new Set(['sun', 'moon']);

export function canGrahaBeRetrograde(grahaKey) {
  return !NAVAGRAHA_NEVER_RETROGRADE.has(grahaKey);
}

/**
 * @param {string} grahaKey
 * @param {{ degree?: number; retrograde?: boolean }} [meta]
 */
export function normalizeGrahaMeta(grahaKey, meta = {}) {
  const degree = meta.degree === '' || meta.degree == null ? 15 : Number(meta.degree) || 0;
  const retrograde = canGrahaBeRetrograde(grahaKey) ? Boolean(meta.retrograde) : false;
  return { degree, retrograde };
}

/** Whole-sign rashi in a bhava from lagna sign. */
export function signFromHouse(lagnaSign, house) {
  const lagna = Number(lagnaSign);
  const h = Number(house);
  if (lagna < 1 || lagna > 12 || h < 1 || h > 12) return null;
  return ((lagna - 1 + h - 1) % 12) + 1;
}

/**
 * @param {{ lagnaSign: number; lagnaDegree?: number; placements: Record<string, number>; grahaMeta?: Record<string, { degree?: number; retrograde?: boolean }> }} input
 */
export function chartStateFromInteractive({ lagnaSign, lagnaDegree = 0, placements, grahaMeta = {} }) {
  const grahas = createInitialNavagrahaEntries().map((entry) => {
    const house = placements[entry.grahaKey];
    if (!house) return entry;
    const meta = grahaMeta[entry.grahaKey] ?? {};
    const normalized = normalizeGrahaMeta(entry.grahaKey, meta);
    return {
      ...entry,
      rashiSign: signFromHouse(lagnaSign, house),
      degree: normalized.degree,
      retrograde: normalized.retrograde,
    };
  });
  return {
    lagna: { sign: lagnaSign, degree: Number(lagnaDegree) || 0 },
    grahas,
  };
}

/**
 * @param {{ lagna?: { sign?: number; degree?: number }; grahas?: Record<string, { sign?: number; degree?: number; retrograde?: boolean }> }} payload
 */
export function interactiveFromPayload(payload) {
  const lagnaSign = payload?.lagna?.sign;
  if (!lagnaSign) {
    return { lagnaSign: 1, lagnaDegree: 0, placements: {}, grahaMeta: {} };
  }
  /** @type {Record<string, number>} */
  const placements = {};
  /** @type {Record<string, { degree: number; retrograde: boolean }>} */
  const grahaMeta = {};
  for (const [key, row] of Object.entries(payload.grahas ?? {})) {
    if (!row?.sign) continue;
    const house = houseFromSign(lagnaSign, row.sign);
    if (house) {
      placements[key] = house;
      grahaMeta[key] = normalizeGrahaMeta(key, {
        degree: Number(row.degree) || 0,
        retrograde: Boolean(row.retrograde),
      });
    }
  }
  return {
    lagnaSign,
    lagnaDegree: Number(payload.lagna?.degree) || 0,
    placements,
    grahaMeta,
  };
}

export function createEmptyInteractiveChart() {
  return {
    lagnaSign: 1,
    lagnaDegree: 0,
    placements: {},
    grahaMeta: {},
  };
}

/**
 * @param {{ lagnaSign: number; placements: Record<string, number> }} input
 */
export function validateInteractiveChart({ lagnaSign, placements }) {
  if (!lagnaSign || lagnaSign < 1 || lagnaSign > 12) {
    return { valid: false, code: 'lagna_required' };
  }
  const placed = Object.keys(placements).length;
  const required = NAVAGRAHA_PLACEMENT_ORDER.length;
  if (placed < required) {
    return { valid: false, code: 'all_grahas', placedCount: placed, requiredCount: required };
  }
  return { valid: true, placedCount: placed };
}

/**
 * Structured chart readout for compact UI (badges + indented bullets).
 * @param {{ lagnaSign: number; lagnaDegree?: number; placements: Record<string, number>; grahaMeta?: Record<string, { degree?: number; retrograde?: boolean }> }} interactive
 */
export function buildInteractiveChartSummary(interactive) {
  const lagnaSign = Number(interactive.lagnaSign) || null;
  const lagnaDegree = Number(interactive.lagnaDegree) || 0;
  const lagnaOpt = LAGNA_RASHI_OPTIONS.find((r) => r.value === lagnaSign);

  /** @type {{ key: string; labelEn: string; labelHi: string; house: number; sign: number; signEn: string; signHi: string; degree: number; retrograde: boolean; detailsEn: string[]; detailsHi: string[] }[]} */
  const grahas = [];

  for (const meta of NAVAGRAHA_CALC_OPTIONS) {
    const house = interactive.placements?.[meta.value];
    if (!house) continue;
    const sign = signFromHouse(lagnaSign, house);
    if (!sign) continue;
    const signOpt = LAGNA_RASHI_OPTIONS.find((r) => r.value === sign);
    const normalized = normalizeGrahaMeta(meta.value, interactive.grahaMeta?.[meta.value] ?? {});
    const degreeText = normalized.degree.toFixed(1);
    grahas.push({
      key: meta.value,
      labelEn: meta.labelEn,
      labelHi: meta.labelHi,
      house: Number(house),
      sign,
      signEn: signOpt?.en ?? String(sign),
      signHi: signOpt?.hi ?? String(sign),
      degree: normalized.degree,
      retrograde: normalized.retrograde,
      detailsEn: [
        `${signOpt?.en ?? sign} (${signOpt?.hi ?? sign}) - sign ${sign}, ${degreeText}°`,
        `House ${house}${normalized.retrograde ? ', retrograde' : ''}`,
      ],
      detailsHi: [
        `${signOpt?.hi ?? sign} (${signOpt?.en ?? sign}) - राशि ${sign}, ${degreeText}°`,
        `भाव ${house}${normalized.retrograde ? ', वक्री' : ''}`,
      ],
    });
  }

  const lagnaLordEn = lagnaSign ? SIGN_LORD_EN[lagnaSign - 1] : null;

  return {
    lagnaSign,
    lagnaDegree,
    lagnaEn: lagnaOpt?.en ?? null,
    lagnaHi: lagnaOpt?.hi ?? null,
    lagnaLordEn,
    grahas,
  };
}

/**
 * Plain-text chart summary for RAG / AI context.
 * @param {{ lagnaSign: number; lagnaDegree?: number; placements: Record<string, number>; grahaMeta?: Record<string, { degree?: number; retrograde?: boolean }> }} interactive
 */
export function interactiveChartSummaryToText(interactive) {
  const summary = buildInteractiveChartSummary(interactive);
  if (!summary.lagnaSign) return '';

  const lines = [
    'D-1 North Indian chart (whole-sign houses).',
    `Lagna: ${summary.lagnaEn} sign ${summary.lagnaSign}, ${Number(summary.lagnaDegree || 0).toFixed(1)}°. Lagna lord: ${summary.lagnaLordEn}.`,
  ];

  for (const g of summary.grahas) {
    const retro = g.retrograde ? ', retrograde' : '';
    lines.push(
      `${g.labelEn} in ${g.signEn} sign ${g.sign}, ${g.degree.toFixed(1)}°${retro}, house ${g.house}.`,
    );
  }

  return lines.join('\n');
}

/**
 * @param {{ lagnaSign: number; lagnaDegree?: number; placements: Record<string, number>; grahaMeta?: Record<string, { degree?: number; retrograde?: boolean }> }} interactive
 * @param {string} userQuestion
 */
export function buildChartRagQuery(interactive, userQuestion) {
  const summary = buildInteractiveChartSummary(interactive);
  const chartTerms = [
    summary.lagnaEn ? `lagna ${summary.lagnaEn}` : '',
    summary.lagnaLordEn ? `lagna lord ${summary.lagnaLordEn}` : '',
    ...(summary.grahas || []).map((g) => `${g.labelEn} ${g.signEn} house ${g.house}`),
    'parashari syllabus',
  ]
    .filter(Boolean)
    .join(' ');

  return `${String(userQuestion).trim()} ${chartTerms}`.trim();
}

/**
 * @param {{ lagnaSign: number; lagnaDegree?: number; placements: Record<string, number>; grahaMeta?: Record<string, { degree?: number; retrograde?: boolean }> }} interactive
 */
export function buildPlanetsByHouseFromInteractive(interactive) {
  const { placements, grahaMeta = {} } = interactive;
  /** @type {Record<number, ReturnType<typeof yogaChartPlanet>[]>} */
  const planetsByHouse = {};

  for (const [grahaKey, house] of Object.entries(placements ?? {})) {
    const bhava = Number(house);
    if (bhava < 1 || bhava > 12) continue;
    const en = GRAHA_EN[grahaKey] ?? grahaKey;
    const meta = normalizeGrahaMeta(grahaKey, grahaMeta[grahaKey] ?? {});
    const planet = yogaChartPlanet(en, meta.degree);
    planet.planetEn = en;
    if (meta.retrograde) {
      planet.retrograde = true;
      planet.suffix = 'R';
    }
    if (!planetsByHouse[bhava]) planetsByHouse[bhava] = [];
    planetsByHouse[bhava].push(planet);
  }

  return planetsByHouse;
}

export const SYLLABUS_CHART_LAB_EXAMPLE = {
  lagna: { sign: 2, degree: 21 },
  grahas: {
    sun: { sign: 3, degree: 2, retrograde: false },
    moon: { sign: 6, degree: 13, retrograde: false },
    mars: { sign: 1, degree: 12, retrograde: false },
    mercury: { sign: 3, degree: 18, retrograde: false },
    jupiter: { sign: 2, degree: 8, retrograde: false },
    venus: { sign: 3, degree: 22, retrograde: false },
    saturn: { sign: 11, degree: 5, retrograde: false },
    rahu: { sign: 9, degree: 14, retrograde: true },
    ketu: { sign: 3, degree: 14, retrograde: true },
  },
};

/**
 * @returns {{ lagna: { sign: number | null; degree: number }; grahas: ReturnType<typeof createInitialNavagrahaEntries> }}
 */
export function createEmptySyllabusChart() {
  return {
    lagna: { sign: null, degree: 0 },
    grahas: createInitialNavagrahaEntries(),
  };
}

/**
 * @param {{ lagna: { sign: number | null; degree: number }; grahas: { grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean }[] }} state
 */
export function syllabusChartToPayload(state) {
  /** @type {Record<string, { sign: number; degree: number; retrograde: boolean }>} */
  const grahas = {};
  for (const entry of state.grahas) {
    if (!entry.rashiSign) continue;
    grahas[entry.grahaKey] = {
      sign: entry.rashiSign,
      degree: Number(entry.degree) || 0,
      retrograde: Boolean(entry.retrograde),
    };
  }
  return {
    lagna: {
      sign: state.lagna.sign,
      degree: Number(state.lagna.degree) || 0,
    },
    grahas,
    houseSystem: 'whole_sign',
    chartType: 'D1',
  };
}

/**
 * @param {{ lagna?: { sign?: number; degree?: number }; grahas?: Record<string, { sign?: number; degree?: number; retrograde?: boolean }> }} payload
 */
export function payloadToSyllabusChart(payload) {
  const base = createEmptySyllabusChart();
  if (payload?.lagna?.sign) {
    base.lagna = {
      sign: payload.lagna.sign,
      degree: Number(payload.lagna.degree) || 0,
    };
  }
  const grahaMap = payload?.grahas ?? {};
  base.grahas = base.grahas.map((entry) => {
    const row = grahaMap[entry.grahaKey];
    if (!row?.sign) return entry;
    return {
      ...entry,
      rashiSign: row.sign,
      degree: Number(row.degree) || 0,
      retrograde: Boolean(row.retrograde),
    };
  });
  return base;
}

/**
 * @param {{ lagna: { sign: number | null; degree: number }; grahas: { grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean }[] }} state
 */
export function validateSyllabusChart(state) {
  if (!state.lagna.sign) {
    return { valid: false, code: 'lagna_required' };
  }
  const placed = state.grahas.filter((g) => g.rashiSign);
  if (placed.length < 2) {
    return { valid: false, code: 'min_grahas' };
  }
  for (const g of placed) {
    if (!isValidDegreeInSign(g.degree)) {
      return { valid: false, code: 'invalid_degree', grahaKey: g.grahaKey };
    }
  }
  return { valid: true, placedCount: placed.length };
}

/**
 * @param {{ lagna: { sign: number | null; degree: number }; grahas: { grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean }[] }} state
 */
export function buildChartDigest(state) {
  const payload = syllabusChartToPayload(state);
  const parts = [`L${payload.lagna.sign ?? 0}:${Math.round(payload.lagna.degree)}`];
  for (const key of NAVAGRAHA_CALC_OPTIONS.map((g) => g.value)) {
    const row = payload.grahas[key];
    if (!row) continue;
    parts.push(`${key}${row.sign}${Math.round(row.degree)}${row.retrograde ? 'R' : ''}`);
  }
  return parts.join('|');
}

/**
 * @param {{ lagna: { sign: number | null; degree: number }; grahas: { grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean }[] }} state
 */
export function buildSyllabusChartModel(state) {
  const lagnaSign = state.lagna.sign;
  if (!lagnaSign) return null;

  /** @type {Record<number, ReturnType<typeof yogaChartPlanet>[]>} */
  const planetsByHouse = {};

  for (const entry of state.grahas) {
    if (!entry.rashiSign) continue;
    const house = houseFromSign(lagnaSign, entry.rashiSign);
    const en = GRAHA_EN[entry.grahaKey] ?? entry.grahaKey;
    const planet = yogaChartPlanet(en, entry.degree);
    if (entry.retrograde) {
      planet.retrograde = true;
      planet.suffix = 'R';
    }
    if (!planetsByHouse[house]) planetsByHouse[house] = [];
    planetsByHouse[house].push(planet);
  }

  return createNorthIndianChartData({
    signStartIndex: lagnaSign - 1,
    planetsByHouse,
  });
}

export function getSyllabusChartValidationMessage(code, showEnglish) {
  switch (code) {
    case 'lagna_required':
      return showEnglish ? 'Select lagna (ascendant sign) first.' : 'पहले लग्न (उदय राशि) चुनें।';
    case 'min_grahas':
      return showEnglish
        ? 'Place at least two grahas before analyzing.'
        : 'विश्लेषण से पहले कम से कम दो ग्रह रखें।';
    case 'all_grahas':
      return showEnglish
        ? 'Place all nine grahas on the chart before explaining topics.'
        : 'विषय समझाने से पहले सभी नौ ग्रह चार्ट पर रखें।';
    case 'invalid_degree':
      return showEnglish ? 'Degree must be 0–29 in each sign.' : 'प्रत्येक राशि में अंश 0–29 होने चाहिए।';
    default:
      return showEnglish ? 'Complete the chart first.' : 'पहले कुंडली पूरी करें।';
  }
}

export function readLocalChartLabDraft() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SYLLABUS_CHART_LAB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {{ chart?: object; readout?: object }} draft
 */
export function writeLocalChartLabDraft(draft) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SYLLABUS_CHART_LAB_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* ignore quota */
  }
}

export function clearLocalChartLabDraft() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(SYLLABUS_CHART_LAB_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export { LAGNA_RASHI_OPTIONS, NAVAGRAHA_CALC_OPTIONS };
